import { ExternalLink, LogOut, UserRound } from 'lucide-react'
import { Link } from 'react-router-dom'

import { cn } from '@/lib/utils'
import { getFileUrl } from '@/clients/http'
import { useAuth } from '@/contexts/AuthContext'
import { useBusiness } from '@/contexts/BusinessContext'
import { useStorefrontUrl } from '@/hooks/useStorefrontUrl'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

/** Merchant logo, or the store's initial on a tile while there is none (and a pulse while the profile loads). */
export function StoreAvatar({ className }: { className?: string }) {
  const { profile, loading } = useBusiness()
  if (profile?.logo_path) {
    return <img src={getFileUrl(profile.logo_path)} alt="" className={cn('size-6 shrink-0 rounded-md bg-card object-contain', className)} />
  }
  return (
    <span
      className={cn(
        'grid size-6 shrink-0 place-items-center rounded-md bg-success-lighter text-xs font-semibold uppercase text-success-dark',
        loading && 'animate-pulse',
        className
      )}
      aria-hidden="true"
    >
      {(profile?.name || '').slice(0, 2)}
    </span>
  )
}

interface AccountMenuProps {
  /** Rail and mobile bar: the avatar alone. */
  compact?: boolean
}

/** Store row pinned at the bottom of the sidebar; opens the account menu (profile, visit store, log out). */
export function AccountMenu({ compact = false }: AccountMenuProps) {
  const { profile, loading } = useBusiness()
  const { user, logout } = useAuth()
  const storefrontUrl = useStorefrontUrl()
  const role = user?.roles?.[0]?.name
  const name = profile?.name || ''

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'flex items-center gap-2 rounded-md text-left text-sm font-medium text-sidebar-foreground outline-none transition-colors hover:bg-sidebar-hover hover:text-white focus-visible:ring-2 focus-visible:ring-sidebar-ring',
          compact ? 'size-9 justify-center' : 'h-8 w-full px-1.5'
        )}
        aria-label="Store and account menu"
      >
        <StoreAvatar />
        {!compact &&
          (loading ? (
            <span className="h-3.5 w-24 animate-pulse rounded bg-sidebar-hover" />
          ) : (
            <span className="min-w-0 flex-1 truncate" title={name}>
              {name}
            </span>
          ))}
      </DropdownMenuTrigger>
      <DropdownMenuContent side={compact ? 'right' : 'top'} align="start" sideOffset={8} className="w-64">
        <DropdownMenuLabel className="flex items-center gap-2.5 py-2 font-normal">
          {user?.image?.path ? (
            <img src={getFileUrl(user.image.path)} alt="" className="size-8 shrink-0 rounded-full object-cover" />
          ) : (
            <span className="grid size-8 shrink-0 place-items-center rounded-full bg-fill text-sm font-semibold uppercase text-ink">
              {user?.name?.charAt(0) ?? '?'}
            </span>
          )}
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium text-ink">{user?.name}</span>
            <span className="block truncate text-xs text-muted-foreground">{user?.email}</span>
            {role && <span className="block truncate text-xs text-text-2">{role}</span>}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/profile">
            <UserRound className="mr-2 size-4" /> Profile & password
          </Link>
        </DropdownMenuItem>
        {storefrontUrl && (
          <DropdownMenuItem asChild>
            <a href={storefrontUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="mr-2 size-4" /> Visit store
            </a>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={logout} className="text-error focus:bg-error-lighter focus:text-error-dark">
          <LogOut className="mr-2 size-4" /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
